import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Setting, SettingMap } from './setting.model';

/**
 * TVG-315: client-side access to admin-editable settings.
 *
 * loadPublic() fetches the map of public settings once and caches it, so UI
 * copy driven from a setting (the text permission opt-in wording) can be read
 * synchronously. list()/update() are the admin Settings page.
 */
@Injectable({ providedIn: 'root' })
export class SettingService {
  private readonly baseUrl = `${environment.apiUrl}/api/settings`;
  private readonly state = new BehaviorSubject<SettingMap>({});
  private loaded = false;
  private inFlight?: Observable<SettingMap>;

  readonly state$ = this.state.asObservable();

  constructor(private http: HttpClient) {}

  loadPublic(force = false): Observable<SettingMap> {
    if (this.loaded && !force) {
      return of(this.state.value);
    }
    if (this.inFlight && !force) {
      return this.inFlight;
    }

    this.inFlight = this.http
      .get<{ data: SettingMap }>(`${this.baseUrl}/public`)
      .pipe(
        map((res) => res.data ?? {}),
        tap((data) => {
          this.state.next(data);
          this.loaded = true;
          this.inFlight = undefined;
        }),
      );

    return this.inFlight;
  }

  /** Cached value for a public key, or null when it has not loaded yet. */
  value(key: string): string | null {
    const value = this.state.value[key];
    return value && value.trim() !== '' ? value : null;
  }

  value$(key: string): Observable<string | null> {
    return this.state$.pipe(map((settings) => settings[key] ?? null));
  }

  clearCache(): void {
    this.loaded = false;
    this.state.next({});
  }

  list(): Observable<Setting[]> {
    return this.http
      .get<{ data: Setting[] }>(this.baseUrl)
      .pipe(map((res) => res.data ?? []));
  }

  update(key: string, value: string | null): Observable<Setting> {
    return this.http
      .put<{ data: Setting }>(`${this.baseUrl}/${encodeURIComponent(key)}`, { value })
      .pipe(
        map((res) => res.data),
        tap((setting) => {
          if (setting.is_public) {
            this.state.next({ ...this.state.value, [setting.key]: setting.value });
          }
        }),
      );
  }
}
